import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import Modal from '../../comun/Modal';
import { Estrella } from '../publico/Iconos';

const baseURL = process.env.REACT_APP_FAST_FOOD_SERVICE;

/**
 * Canje de regalos en el POS: el cajero busca por cédula, ve el saldo y entrega el regalo.
 */
const RegalosModal = ({ isOpen, onClose, isFeria = false }) => {
    const [cedula, setCedula] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [redeemingId, setRedeemingId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setCedula('');
            setData(null);
            setError('');
            setSuccess('');
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const lookup = async (value = cedula) => {
        const clean = value.replace(/\D/g, '');
        if (clean.length < 10) {
            setError('Ingresa una cédula válida (10 dígitos).');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/api/loyalty/customers/lookup/', { baseURL, params: { cedula: clean } });
            setData(res.data);
        } catch (err) {
            setData(null);
            setError(err.response?.data?.error || 'No se pudo consultar la cédula.');
            if (err.response?.status !== 404) console.error('[Regalos] Error consultando cédula', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRedeem = async (reward) => {
        if (!data) return;
        const ok = window.confirm(
            `¿Entregar "${reward.name}" a ${data.customer.full_name}?\n\nSe descontarán ${reward.points_cost} puntos.`
        );
        if (!ok) return;

        setRedeemingId(reward.id);
        setError('');
        setSuccess('');
        try {
            const res = await api.post('/api/loyalty/redemptions/', {
                customer_id: data.customer.id,
                reward_id: reward.id,
                is_feria: isFeria,
            }, { baseURL });
            setSuccess(`${reward.name} entregado. Saldo nuevo: ${res.data.points_balance} puntos.`);
            await lookup(data.customer.cedula || cedula);
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo registrar el canje.');
            console.error('[Regalos] Error en canje', err);
        } finally {
            setRedeemingId(null);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Regalos por puntos" maxWidth="640px">
            <form
                onSubmit={(e) => { e.preventDefault(); lookup(); }}
                style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}
            >
                <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    placeholder="Cédula del cliente"
                    value={cedula}
                    maxLength={13}
                    onChange={(e) => setCedula(e.target.value.replace(/\D/g, ''))}
                    className="form-control"
                    style={{ flex: 1, fontSize: '1.1rem', padding: '0.75rem' }}
                />
                <button type="submit" className="btn btn-primary" disabled={loading} style={{ minWidth: '120px' }}>
                    {loading ? 'Buscando…' : 'Consultar'}
                </button>
            </form>

            {error && (
                <div style={{ background: '#fdecec', color: '#9b1c1c', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem' }}>
                    {error}
                </div>
            )}
            {success && (
                <div style={{ background: '#e6f6ee', color: '#1f6b45', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', fontWeight: 600 }}>
                    {success}
                </div>
            )}

            {data && (
                <>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem',
                        background: 'var(--sidebar-bg)', borderRadius: '12px', padding: '1rem', marginBottom: '1rem'
                    }}>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--primary-color)' }}>
                                {data.customer.full_name}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                                C.I. {data.customer.cedula} {data.customer.phone && `· ${data.customer.phone}`}
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--primary-color)', lineHeight: 1 }}>
                                {data.points_balance}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>puntos</div>
                        </div>
                    </div>

                    {data.rewards.length === 0 ? (
                        <p style={{ color: '#6b7280', textAlign: 'center' }}>
                            No hay regalos configurados. Créalos en el módulo Puntos.
                        </p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {data.rewards.map((r) => (
                                <div key={r.id} style={{
                                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                                    border: `2px solid ${r.can_claim ? 'var(--primary-color)' : '#e5e7eb'}`,
                                    borderRadius: '10px', padding: '0.75rem'
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 700 }}>{r.name}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                            <Estrella size={13} style={{ verticalAlign: '-2px', color: '#d9a441' }} /> {r.points_cost} puntos
                                            {!r.in_stock && ' · Agotado'}
                                            {r.in_stock && !r.can_claim && ` · Le faltan ${r.points_missing}`}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={!r.can_claim || redeemingId !== null}
                                        onClick={() => handleRedeem(r)}
                                        style={{ minWidth: '110px', opacity: r.can_claim ? 1 : 0.5 }}
                                    >
                                        {redeemingId === r.id ? 'Entregando…' : 'Entregar'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {data.history.length > 0 && (
                        <details style={{ marginTop: '1rem' }}>
                            <summary style={{ cursor: 'pointer', color: '#6b7280' }}>Últimos movimientos</summary>
                            <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0' }}>
                                {data.history.map((h, i) => (
                                    <li key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid #f1f1f1', fontSize: '0.9rem' }}>
                                        <span>{h.reason} · {new Date(h.created_at).toLocaleDateString('es-EC')}</span>
                                        <strong style={{ color: h.points_change >= 0 ? '#1f6b45' : 'var(--primary-color)' }}>
                                            {h.points_change >= 0 ? '+' : ''}{h.points_change}
                                        </strong>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </>
            )}
        </Modal>
    );
};

export default RegalosModal;
